import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import moment from 'moment-timezone';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { ConfirmDialog, DataTable, DateField, EmptyState, PageHeader, StatCard } from '@routes-plan/ui';
import { useSelection } from '../../../components/context/SelectionContext';
import { Route } from '../../../models/route';
import { Office } from '../../../models/office';
import { nowOperationalDateString, operationalTimezone } from '../../../utils/operationalDateTime';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { formatCurrency } from '../../../utils/format/currency';
import RowAction from '../components/RowAction';
import OfficeRouteSelects from '../components/OfficeRouteSelects';
import { movementColumns, movementTotals, type MovementRow } from '../components/movements';
import { filterGridSx, pageSx, sectionSx, statGridSx } from '../components/layout';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

interface ExpenseDetail {
    id: number;
    date: string;
    createdAt?: string | Date;
    description: string;
    value: number;
    balance: number;
    originCashBox: {
        id: number;
        balance: number;
        date: string;
        route: {
            id: number;
            name: string;
        } | null; // route puede ser null
    } | null; // originCashBox puede ser null
    destinationCashBox: {
        id: number;
        balance: number;
        date: string;
        route: {
            id: number;
            name: string;
        } | null; // route puede ser null
    } | null; // destinationCashBox puede ser null
    expenseType: {
        id: number;
        name: string;
    };
    operationType: {
        id: number;
        name: string;
    };
    routeName?: string;
}

const CashBoxExpenseList: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
    const t = useT();
    setPageTitle('cashBoxes.movementsTitle');
    const [pendingDelete, setPendingDelete] = useState<number | null>(null);
    const { canDeleteBoxMovement } = useResourcePermissions('/loans/cash-box-expenses');
    const [expensesDetail, setExpensesDetail] = useState<ExpenseDetail[]>([]);
    const { office_id: officeId, setOffice_id: setOfficeId, collect_id: routeId, setCollect_id: setRouteId } = useSelection();
    const todayOperational = nowOperationalDateString();
    // Rango editable (UI)
    const [startDate, setStartDate] = useState<string>(todayOperational);
    const [endDate, setEndDate] = useState<string>(todayOperational);
    const [opFilter, setOpFilter] = useState<'all' | 'sum' | 'rest'>('all');
    const [routeFilter, setRouteFilter] = useState<'all' | string>('all');
    const [onlyPrincipal, setOnlyPrincipal] = useState<boolean>(false);

    const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
    const [offices, setOffices] = useState<Office[]>([]);

    // Cargar sedes y datos relacionados con officeId

    useEffect(() => {
        const fetchOffice = async () => {
            try {
                showLoading();
                setExpensesDetail([]); // Limpiar los detalles si no hay routeId
                const { data } = await axios.get('offices');
                setOffices(data.data);
                await new Promise((resolve) => setTimeout(resolve, 1000));
                showAlert(t('cashBoxes.queryOk'), 'info');
            } catch (error) {
                showAlert(t('cashBoxes.officesError'), 'error');
            } finally {
                hideLoading();
            }
        };
        fetchOffice();
    }, []); // Solo depende de officeId

    useEffect(() => {
        // Si officeId no tiene un valor válido, no ejecutar el efecto
        if (!officeId) {
            return;
        }

        // Al cambiar de sede, se reinicia el filtro de ruta (la lista y los permisos cambian).
        setRouteFilter('all');
        setOnlyPrincipal(false);

        const fetchDataDetailOffice = async () => {
            showLoading();
            try {
                setExpensesDetail([]); // Limpiar los detalles si no hay routeId
                const officesResponse = axios.get('offices');
                const officeRoutesResponse = officeId ? axios.get(`offices/${officeId}/routes`) : null;
                const { data } = await axios.get(`offices/${officeId}/expenses`, {
                    params: { startDate: todayOperational, endDate: todayOperational },
                });
                setExpensesDetail(Array.isArray(data) ? data : []);

                const [officesData, officeRoutesData] = await Promise.all([
                    officesResponse,
                    officeRoutesResponse,
                ]);

                if (officesData) setOffices(officesData.data.data);
                if (officeRoutesData) setOfficeRoutes(officeRoutesData.data);

                await new Promise((resolve) => setTimeout(resolve, 1000));
            } catch (error) {
                showAlert(t('common.loadError'), 'error');
            } finally {
                hideLoading();
            }
        };

        fetchDataDetailOffice();
    }, [officeId, todayOperational]); // Solo depende de officeId

    // Si venimos desde "loans/cash-boxes" con una ruta seleccionada, dejarla en el filtro.
    // Si es caja principal (routeId vacío), mantener "Todos".
    useEffect(() => {
        const numeric = Number(routeId);
        if (routeId && Number.isFinite(numeric) && numeric > 0) {
            setOnlyPrincipal(false);
            setRouteFilter(String(numeric));
        } else {
            setRouteFilter('all');
        }
    }, [routeId]);
    const BoxOfficeMovemente = async () => {
        showLoading();
        try {
            setExpensesDetail([]); // Limpiar los detalles si no hay routeId
            const officesResponse = axios.get('offices');


            const [officesData] = await Promise.all([
                officesResponse,
            ]);

            if (officesData) setOffices(officesData.data.data);

            await new Promise((resolve) => setTimeout(resolve, 1000));
            showAlert(t('cashBoxes.queryOk'), 'info');

        } catch (error) {
            showAlert(t('common.loadError'), 'error');
        } finally {
            hideLoading();
        }
    };

    const formatDate = (value: string | number | undefined) => {
        if (!value) return '-';
        try {
            return format(parseISO(String(value)), 'yyyy-MM-dd HH:mm:ss');
        } catch (error) {
            return String(value);
        }
    };

    const formatDateTimeBogota = (row: MovementRow) => {
        const raw = (row as any)?.createdAt || row.date;
        if (!raw) return '-';
        const m = moment.tz(raw, operationalTimezone());
        if (m.isValid()) {
            return m.format('YYYY-MM-DD HH:mm');
        }
        return formatDate(String(raw));
    };

    const isTodayOperational = (row: MovementRow) => {
        const today = nowOperationalDateString(); // YYYY-MM-DD (Bogotá)
        const raw = (row as any)?.createdAt || row.date;
        const m = moment.tz(raw, ['YYYY-MM-DD', moment.ISO_8601], operationalTimezone());
        if (!m.isValid()) return false;
        return m.format('YYYY-MM-DD') === today;
    };

    const handleDeleteMovement = (row: MovementRow) => {
        if (!canDeleteBoxMovement) {
            return;
        }
        const id = Number(row.id);
        if (!id || !Number.isFinite(id)) {
            showAlert(t('cashBoxes.movementIdError'), 'warning');
            return;
        }
        if (!isTodayOperational(row)) {
            showAlert(t('cashBoxes.onlyTodayDelete'), 'warning');
            return;
        }
        setPendingDelete(id);
    };

    const confirmDeleteMovement = async () => {
        const id = pendingDelete;
        setPendingDelete(null);
        if (id == null) return;
        showLoading();
        try {
            await axios.delete(`/cash-movements/${id}`);
            showAlert(t('cashBoxes.movementDeleted'), 'success');
            if (officeId) {
                const { data } = await axios.get(`offices/${officeId}/expenses`, {
                    params: { startDate, endDate },
                });
                setExpensesDetail(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error(error);
            const msg =
                (error as any)?.response?.data?.message ||
                t('cashBoxes.movementDeleteError');
            showAlert(msg, 'error');
        } finally {
            hideLoading();
        }
    };

    const rows: MovementRow[] = expensesDetail.map((expenseDetail) => ({
        id: expenseDetail.id,
        expenseName: expenseDetail.expenseType.name,
        operationTypeId: expenseDetail.operationType?.id,
        operationTypeName: expenseDetail.operationType.name,
        routeId:
            expenseDetail.originCashBox?.route?.id ??
            expenseDetail.destinationCashBox?.route?.id ??
            null,
        routeName: expenseDetail.originCashBox?.route?.name || expenseDetail.destinationCashBox?.route?.name || expenseDetail.routeName || '-',
        value: expenseDetail.value ?? 0,
        description: expenseDetail.description,
        date: expenseDetail.date,
        createdAt: (expenseDetail as any)?.createdAt || undefined,
    }));

    const filteredRows = useMemo(() => {
        let next = rows;

        if (opFilter !== 'all') {
            const wantTypeOpId = opFilter === 'sum' ? 1 : 2;
            next = next.filter((row) => Number(row.operationTypeId) === wantTypeOpId);
        }

        if (onlyPrincipal) {
            next = next.filter((row) => row.routeId == null);
        } else if (routeFilter !== 'all') {
            const wantRouteId = Number(routeFilter);
            next = next.filter((row) => Number(row.routeId) === wantRouteId);
        }

        return next;
    }, [rows, opFilter, routeFilter, onlyPrincipal]);

    const fetchMovementsForRange = async (rangeStart: string, rangeEnd: string) => {
        if (!officeId) {
            showAlert(t('cashBoxes.selectOfficeWarning'), 'warning');
            return;
        }
        showLoading();
        try {
            const { data } = await axios.get(`offices/${officeId}/expenses`, {
                params: { startDate: rangeStart, endDate: rangeEnd },
            });
            const list = Array.isArray(data) ? data : [];
            setExpensesDetail(list);
            if (list.length === 0) {
                showAlert(t('cashBoxes.noRecordsRange'), 'info');
            }
        } catch (error) {
            console.error(error);
            const msg =
                (error as any)?.response?.data?.message ||
                t('cashBoxes.movementsLoadError');
            showAlert(msg, 'error');
            setExpensesDetail([]);
        } finally {
            hideLoading();
        }
    };

    const handleApplyDateRange = async () => {
        if (!startDate || !endDate) {
            showAlert(t('cashBoxes.selectDateRange'), 'warning');
            return;
        }
        const startM = moment.tz(startDate, ['YYYY-MM-DD'], operationalTimezone());
        const endM = moment.tz(endDate, ['YYYY-MM-DD'], operationalTimezone());
        if (!startM.isValid() || !endM.isValid()) {
            showAlert(t('cashBoxes.invalidDateRange'), 'warning');
            return;
        }
        if (endM.isBefore(startM, 'day')) {
            showAlert(t('cashBoxes.endBeforeStart'), 'warning');
            return;
        }
        await fetchMovementsForRange(startDate, endDate);
    };

    const columns = movementColumns(t, formatDateTimeBogota);
    const totals = movementTotals(filteredRows);

    return (
        <Box sx={pageSx}>
            <PageHeader
                title={t('cashBoxes.movementsTitle')}
                description={t('cashBoxes.movementsDescription')}
                breadcrumbs={[
                    { label: t('cashBoxes.listTitle'), href: '#/loans/cash-boxes' },
                    { label: t('cashBoxes.movementsTitle') },
                ]}
                actions={
                    <Button variant="outlined" startIcon={<ArrowBackOutlined />} component={NavLink} to="/loans/cash-boxes">
                        {t('common.back')}
                    </Button>
                }
            />
            <Paper variant="outlined" component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); void handleApplyDateRange(); }} sx={sectionSx}>
                <Box sx={filterGridSx}>
                    <OfficeRouteSelects
                        offices={offices}
                        officeId={officeId}
                        onOfficeChange={(value) => {
                            // Al cambiar de sede se limpia la ruta seleccionada para no arrastrar una de otra sede.
                            setOfficeId(value);
                            setRouteId('');
                            setRouteFilter('all');
                            setOnlyPrincipal(false);
                        }}
                        routes={officeRoutes}
                        routeId={routeFilter}
                        routeAllOption={{ value: 'all', label: t('common.all') }}
                        onRouteChange={(value) => setRouteFilter(value as any)}
                        routeDisabled={onlyPrincipal}
                    />
                    <DateField size="small" fullWidth label={t('cashBoxes.startDate')} value={startDate} onChange={(v) => setStartDate(v ?? '')} />
                    <DateField size="small" fullWidth label={t('cashBoxes.endDate')} value={endDate} onChange={(v) => setEndDate(v ?? '')} />
                    <TextField
                        select
                        size="small"
                        fullWidth
                        label={t('cashBoxes.type')}
                        value={opFilter}
                        onChange={(e) => setOpFilter(e.target.value as any)}
                    >
                        <MenuItem value="all">{t('common.all')}</MenuItem>
                        <MenuItem value="sum">{t('cashBoxes.sum')}</MenuItem>
                        <MenuItem value="rest">{t('cashBoxes.subtract')}</MenuItem>
                    </TextField>
                    <FormControlLabel
                        control={<Checkbox checked={onlyPrincipal} onChange={(e) => setOnlyPrincipal(e.target.checked)} />}
                        label={t('cashBoxes.onlyMainCashBox')}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gridColumn: '-2 / -1' }}>
                        <Button type="submit" variant="contained" startIcon={<SearchOutlined />}>
                            {t('common.search')}
                        </Button>
                    </Box>
                </Box>
            </Paper>
            {filteredRows.length > 0 ? (
                <Box sx={statGridSx}>
                    <StatCard label={t('expenses.statMovements')} value={filteredRows.length} />
                    <StatCard label={t('expenses.statIn')} value={formatCurrency(totals.in)} />
                    <StatCard label={t('expenses.statOut')} value={formatCurrency(totals.out)} />
                </Box>
            ) : null}
            <DataTable<MovementRow>
                columns={columns}
                rows={filteredRows}
                getRowId={(row) => row.id}
                searchable
                exportFileName={t('cashBoxes.movementsExportFile')}
                ariaLabel={t('cashBoxes.movementsTitle')}
                rowActions={(row) => (
                    <RowAction
                        label={t('cashBoxes.deleteMovement')}
                        icon={<DeleteOutlineOutlined fontSize="small" />}
                        color="error"
                        allowed={canDeleteBoxMovement}
                        deniedLabel={t('cashBoxes.noPermissionDeleteMovement')}
                        disabled={!isTodayOperational(row)}
                        disabledLabel={t('cashBoxes.onlyTodayDelete')}
                        onClick={() => void handleDeleteMovement(row)}
                    />
                )}
                emptyState={<EmptyState dense icon={<ReceiptLongOutlined />} title={t('cashBoxes.emptyMovements')} description={t('expenses.emptyHint')} />}
            />
            <ConfirmDialog
                open={pendingDelete != null}
                variant="danger"
                title={t('cashBoxes.deleteMovement')}
                message={t('cashBoxes.deleteConfirm')}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={() => void confirmDeleteMovement()}
                onCancel={() => setPendingDelete(null)}
            />
        </Box>
    );
};

export default CashBoxExpenseList;
